/**
 * Counting visits: which pages are shown and what is reached (docs/seo.md).
 *
 * Nothing is counted until the visitor agrees (consent.ts), and only with a counter that
 * the server names for this host (config.ts): the team's hosts name none. The services are
 * providers (provider.ts); to add one, write it beside yandexMetrika.ts and list it below.
 *
 * What a counter is told of an address is stripped of what is the visitor's own: the
 * query (a link from a mail carries a token in it) but the language, and the code of an
 * invitation.
 */
import { readAnalyticsConfig, type AnalyticsConfig } from './config';
import { readConsent, type Consent } from './consent';
import type { AnalyticsProvider, Counter, Goal } from './provider';
import { yandexMetrika } from './yandexMetrika';

export type { Goal } from './provider';

export const PROVIDERS: readonly AnalyticsProvider[] = [yandexMetrika];

interface Where {
  origin: string;
  pathname: string;
  search: string;
}

/** An address as a counter is told it. */
export function countedUrl({ origin, pathname, search }: Where): string {
  const lang = new URLSearchParams(search).get('lang');
  const path = pathname.replace(/^\/join\/[^/]+/, '/join/_');
  return `${origin}${path}${lang ? `?lang=${encodeURIComponent(lang)}` : ''}`;
}

export class Analytics {
  private counters: Counter[] | null = null;
  private last: string | undefined;

  constructor(
    private readonly config: () => AnalyticsConfig | null = readAnalyticsConfig,
    private readonly consent: () => Consent | null = readConsent,
    private readonly providers: readonly AnalyticsProvider[] = PROVIDERS,
  ) {}

  /** Whether this page has a counter at all: only then is the visitor asked. */
  get configured(): boolean {
    const config = this.config();
    return config !== null && this.providers.some((provider) => provider.name in config);
  }

  /** Start the counters, if there are any and the visitor agreed. Whether they run. */
  start(): boolean {
    if (this.counters) return true;
    const config = this.config();
    if (!config || this.consent() !== 'all') return false;
    this.counters = this.providers
      .map((provider) => provider.start(config))
      .filter((counter): counter is Counter => counter !== null);
    return true;
  }

  /** A page is shown. The same address twice in a row is one visit. */
  page(where: Where = window.location): void {
    if (!this.start()) return;
    const url = countedUrl(where);
    if (url === this.last) return;
    const from = this.last ?? (document.referrer || undefined);
    for (const counter of this.counters ?? []) counter.page(url, from);
    this.last = url;
  }

  goal(name: Goal): void {
    if (!this.start()) return;
    for (const counter of this.counters ?? []) counter.goal(name);
  }
}

export const analytics = new Analytics();
